/**
 * The wire contract. The API validates every request body against these; the
 * web imports the same objects to build forms and infer response types.
 *
 * One definition, two consumers. A field that changes shape breaks the
 * typecheck on both sides in the same commit, which is the entire point of
 * putting this in a shared package rather than duplicating it.
 */
import { MINUTES_MAX, MINUTES_MIN, MINUTES_STEP } from './hours.js';
import { z } from 'zod';

import { LANES, ROLES, STAGES, STORY_POINTS, TIME_ENTRY_STATUSES, isStageValidForLane } from './enums.js';
import { isPlainDate, isWeekStart } from './week.js';

/* -------------------------------------------------------------------------- */
/* Primitives                                                                 */
/* -------------------------------------------------------------------------- */

export const idSchema = z.string().uuid();

export const plainDateSchema = z
  .string()
  .refine(isPlainDate, { message: 'Expected a YYYY-MM-DD calendar date' });

export const weekStartSchema = plainDateSchema.refine(isWeekStart, {
  message: 'A planned week must be a Monday',
});

export const emailSchema = z.string().trim().toLowerCase().email().max(254);

export const shortTextSchema = z.string().trim().min(1).max(200);
export const longTextSchema = z.string().trim().max(5000);

export const centsSchema = z.number().int().nonnegative();

export const slugSchema = z
  .string()
  .trim()
  .max(60)
  .regex(/^[a-z0-9-]+$/, 'Lowercase letters, numbers and hyphens only');

export const laneSchema = z.enum(LANES);
export const stageSchema = z.enum(STAGES);
export const roleSchema = z.enum(ROLES);
export const timeEntryStatusSchema = z.enum(TIME_ENTRY_STATUSES);

export const storyPointsSchema = z
  .number()
  .refine((n) => (STORY_POINTS as readonly number[]).includes(n), {
    message: 'Story points must be a half-step between 0.5 and 5',
  });

/**
 * The lane/stage invariant as a Zod refinement, so an invalid pair is rejected
 * at the edge and never reaches the CHECK constraint. Both layers keep it,
 * because the edge gives a good error message and the constraint gives a
 * guarantee.
 */
function refineLaneStage<S extends z.ZodTypeAny>(schema: S): z.ZodEffects<S> {
  return schema.superRefine((value: unknown, ctx: z.RefinementCtx) => {
    const v = value as { lane?: unknown; stage?: unknown };
    if (v.lane === undefined) return;
    if (!isStageValidForLane(v.lane as never, (v.stage ?? null) as never)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stage'],
        message: `Stage ${JSON.stringify(v.stage ?? null)} is not valid in lane ${JSON.stringify(v.lane)}`,
      });
    }
  });
}

/* -------------------------------------------------------------------------- */
/* Auth                                                                       */
/* -------------------------------------------------------------------------- */

export const requestCodeSchema = z.object({ email: emailSchema });

export const verifyCodeSchema = z.object({
  email: emailSchema,
  code: z.string().trim().regex(/^\d{6}$/, 'A sign-in code is six digits'),
});

/* -------------------------------------------------------------------------- */
/* People                                                                     */
/* -------------------------------------------------------------------------- */

export const createUserSchema = z
  .object({
    email: emailSchema,
    name: shortTextSchema,
    role: roleSchema,
    /** Required for `client` users, forbidden for everyone else. */
    clientId: idSchema.nullable().default(null),
    weeklyCapacityPoints: z.number().min(0).max(40).default(5),
    title: shortTextSchema.nullable().default(null),
  })
  .superRefine((value, ctx) => {
    if (value.role === 'client' && value.clientId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clientId'],
        message: 'A client user must belong to a client',
      });
    }
    if (value.role !== 'client' && value.clientId !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clientId'],
        message: 'Only client users belong to a client',
      });
    }
  });

export const updateUserSchema = z
  .object({
    name: shortTextSchema.optional(),
    title: shortTextSchema.nullable().optional(),
    weeklyCapacityPoints: z.number().min(0).max(40).optional(),
    isActive: z.boolean().optional(),
    email: emailSchema.optional(),
    role: roleSchema.optional(),
    clientId: idSchema.nullable().optional(),
  })
  /*
   * The same pairing `createUserSchema` enforces, for the same reason: the
   * CHECK `users_client_role_consistency` holds whichever route wrote the row.
   * Only checked when the role is actually being changed - an edit to a name
   * must not have to repeat a clientId it is not touching.
   */
  .superRefine((value, ctx) => {
    if (value.role === undefined) return;

    if (value.role === 'client' && (value.clientId === null || value.clientId === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clientId'],
        message: 'A client user must belong to a client',
      });
    }
    if (value.role !== 'client' && value.clientId !== null && value.clientId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clientId'],
        message: 'Only client users belong to a client',
      });
    }
  });

/* -------------------------------------------------------------------------- */
/* Work hierarchy: client > application > project > feature > task            */
/* -------------------------------------------------------------------------- */

export const createClientSchema = z.object({
  name: shortTextSchema,
  slug: slugSchema,
  summary: longTextSchema.default(''),
});

export const updateClientSchema = createClientSchema.partial().extend({
  isActive: z.boolean().optional(),
});

/**
 * Onboarding a client, in one submit.
 *
 * `client > application > project > feature > task` has no optional levels, so
 * a new client needed five creates before a single task could exist - four of
 * them structural, and none of them the thing anybody came to do.
 * `docs/FINDINGS.md` F8.
 *
 * The levels are **not** made optional, and none of them is invented for the
 * caller. ADR-0003 says an application earns its place because spend and ROI
 * roll up to it, and ADR-0007 holds the chain together with composite keys; a
 * silently-created "General" application would show up in a client's own spend
 * report under a name nobody chose. So the shape stays and the *path* changes:
 * every level is still named, once, on one form.
 */
export const setUpClientSchema = z.object({
  name: shortTextSchema,
  slug: slugSchema,
  summary: longTextSchema.default(''),
  applicationName: shortTextSchema,
  projectName: shortTextSchema,
  featureName: shortTextSchema,
});

export const createApplicationSchema = z.object({
  clientId: idSchema,
  name: shortTextSchema,
  slug: slugSchema,
  summary: longTextSchema.default(''),
  repoUrl: z.string().trim().url().nullable().default(null),
  productionUrl: z.string().trim().url().nullable().default(null),
});

export const updateApplicationSchema = createApplicationSchema
  .omit({ clientId: true })
  .partial()
  .extend({ isActive: z.boolean().optional() });

export const createProjectSchema = z.object({
  applicationId: idSchema,
  name: shortTextSchema,
  summary: longTextSchema.default(''),
  startsOn: plainDateSchema.nullable().default(null),
  targetOn: plainDateSchema.nullable().default(null),
});

export const updateProjectSchema = createProjectSchema
  .omit({ applicationId: true })
  .partial()
  .extend({ isArchived: z.boolean().optional() });

export const createFeatureSchema = z.object({
  projectId: idSchema,
  name: shortTextSchema,
  summary: longTextSchema.default(''),
  /** What the client is told this delivers. Shown on the client dashboard. */
  clientOutcome: longTextSchema.default(''),
});

export const updateFeatureSchema = createFeatureSchema
  .omit({ projectId: true })
  .partial()
  .extend({ orderIndex: z.number().int().min(0).optional() });

export const createTaskSchema = refineLaneStage(
  z.object({
    featureId: idSchema,
    title: shortTextSchema,
    description: longTextSchema.default(''),
    lane: laneSchema.default('backlog'),
    stage: stageSchema.nullable().default(null),
    storyPoints: storyPointsSchema.nullable().default(null),
    assigneeId: idSchema.nullable().default(null),
    plannedWeek: weekStartSchema.nullable().default(null),
    isBlocked: z.boolean().default(false),
    blockedReason: longTextSchema.nullable().default(null),
  }),
);

export const updateTaskSchema = refineLaneStage(
  z.object({
    /**
     * Moving the task to another feature, and so to another project,
     * application and possibly client.
     *
     * Structural rather than an edit, and the route treats it that way: only
     * an owner may change it, and a task with hours logged against it cannot
     * cross a client boundary at all. See `routes/entities.ts`.
     */
    featureId: idSchema.optional(),
    title: shortTextSchema.optional(),
    description: longTextSchema.optional(),
    lane: laneSchema.optional(),
    stage: stageSchema.nullable().optional(),
    storyPoints: storyPointsSchema.nullable().optional(),
    assigneeId: idSchema.nullable().optional(),
    plannedWeek: weekStartSchema.nullable().optional(),
    isBlocked: z.boolean().optional(),
    blockedReason: longTextSchema.nullable().optional(),
    orderIndex: z.number().int().min(0).optional(),
  }),
);

/**
 * A board drag. Deliberately separate from `updateTaskSchema` because a drop
 * sets lane, stage and position together and must be applied atomically -- a
 * partial apply leaves a card in a lane it does not belong in.
 */
export const moveTaskSchema = refineLaneStage(
  z.object({
    lane: laneSchema,
    stage: stageSchema.nullable().default(null),
    /** Position within the destination column, 0-based. */
    position: z.number().int().min(0).default(0),
  }),
);

/* -------------------------------------------------------------------------- */
/* Hours                                                                      */
/* -------------------------------------------------------------------------- */

export const createTimeEntrySchema = z.object({
  taskId: idSchema,
  workDate: plainDateSchema,
  /**
   * Quarter-hour granularity, matching the existing Time Tracker.
   *
   * The bounds and the step come from `hours.ts`, which is also what the
   * stepper controls read. Repeating the numbers here is how a control that
   * offers to change this value ends up offering values it rejects.
   */
  minutes: z
    .number()
    .int()
    .min(MINUTES_MIN)
    .max(MINUTES_MAX)
    .refine((m) => m % MINUTES_STEP === 0, { message: 'Time is logged in quarter-hour steps' }),
  notes: longTextSchema.default(''),
});

export const updateTimeEntrySchema = createTimeEntrySchema.partial();

export const reviewTimeEntrySchema = z.object({
  status: z.enum(['approved', 'rejected']),
  /**
   * What the client is billed, which may differ from what the contractor
   * logged. Null means bill exactly what was logged. Owner-only.
   */
  billableMinutes: z.number().int().min(0).max(960).nullable().default(null),
  reviewNote: longTextSchema.default(''),
});

/* -------------------------------------------------------------------------- */
/* Money (schema present; the screens are Tier B)                             */
/* -------------------------------------------------------------------------- */

export const createPayRateSchema = z.object({
  userId: idSchema,
  hourlyRateCents: centsSchema,
  effectiveFrom: plainDateSchema,
});

export const createBillingRateSchema = z.object({
  clientId: idSchema,
  /** Null is the default rate for the client; a value overrides one person. */
  userId: idSchema.nullable().default(null),
  hourlyRateCents: centsSchema,
  effectiveFrom: plainDateSchema,
});

/**
 * An invoice covers a window, not a month.
 *
 * BotGuide bills Bailey in four-week packets — invoice 0014 runs Jul 27 to
 * Aug 21. An earlier version of this schema took a `YYYY-MM` period, which
 * could not represent that window and made two invoices in one calendar month
 * impossible. Both are ordinary in the real billing history.
 */
export const createInvoiceSchema = z
  .object({
    clientId: idSchema,
    periodStart: plainDateSchema,
    periodEnd: plainDateSchema,
    totalCents: centsSchema,
    issuedOn: plainDateSchema,
    note: longTextSchema.default(''),
  })
  .superRefine((value, ctx) => {
    if (value.periodEnd < value.periodStart) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['periodEnd'],
        message: 'A billing window cannot end before it starts',
      });
    }
  });

export const setBenefitSchema = z
  .object({
    applicationId: idSchema,
    lowCents: centsSchema.nullable().default(null),
    highCents: centsSchema.nullable().default(null),
    note: longTextSchema.default(''),
  })
  .superRefine((value, ctx) => {
    if (value.lowCents !== null && value.highCents !== null && value.lowCents > value.highCents) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['highCents'],
        message: 'High benefit must not be below low benefit',
      });
    }
  });

/* -------------------------------------------------------------------------- */
/* Query parameters                                                           */
/* -------------------------------------------------------------------------- */

/**
 * A boolean that arrived in a query string.
 *
 * NOT `z.coerce.boolean()`. That coerces by JavaScript truthiness, so every
 * non-empty string is true - including `'false'` and `'0'`. A caller writing
 * `?blockedOnly=false` would have got a board filtered down to blocked tasks
 * only, which is the opposite of what they asked for, with no error anywhere.
 *
 * Only the affirmative spellings are true. Anything else is false, because a
 * query parameter nobody can spell correctly should fail safe rather than
 * silently invert a filter.
 */
const queryBooleanSchema = z
  .union([z.boolean(), z.string()])
  .transform((value) =>
    typeof value === 'boolean' ? value : ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase()),
  );

export const boardQuerySchema = z.object({
  clientId: idSchema.optional(),
  applicationId: idSchema.optional(),
  projectId: idSchema.optional(),
  assigneeId: idSchema.optional(),
  plannedWeek: weekStartSchema.optional(),
  blockedOnly: queryBooleanSchema.optional(),
});

export const capacityQuerySchema = z.object({
  weekStart: weekStartSchema.optional(),
  weeks: z.coerce.number().int().min(1).max(12).default(4),
});

export const timesheetQuerySchema = z.object({
  weekStart: weekStartSchema.optional(),
  userId: idSchema.optional(),
  status: timeEntryStatusSchema.optional(),
});

/* -------------------------------------------------------------------------- */
/* Inferred input types                                                       */
/* -------------------------------------------------------------------------- */

export type RequestCodeInput = z.infer<typeof requestCodeSchema>;
export type VerifyCodeInput = z.infer<typeof verifyCodeSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;
export type CreateClientInput = z.infer<typeof createClientSchema>;
export type CreateApplicationInput = z.infer<typeof createApplicationSchema>;
export type CreateProjectInput = z.infer<typeof createProjectSchema>;
export type CreateFeatureInput = z.infer<typeof createFeatureSchema>;
export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;
export type MoveTaskInput = z.infer<typeof moveTaskSchema>;
export type CreateTimeEntryInput = z.infer<typeof createTimeEntrySchema>;
export type ReviewTimeEntryInput = z.infer<typeof reviewTimeEntrySchema>;
export type CreatePayRateInput = z.infer<typeof createPayRateSchema>;
export type CreateBillingRateInput = z.infer<typeof createBillingRateSchema>;
export type BoardQuery = z.infer<typeof boardQuerySchema>;
export type CapacityQuery = z.infer<typeof capacityQuerySchema>;
export type TimesheetQuery = z.infer<typeof timesheetQuerySchema>;
